/**
 * @class constructs an element held in an object.
 * @version 1.4
 */
class Element
{
    /**
     * @param {object} - object of element properties:
     * - classes: array of classes or string of single class/multiple classes seperated by spaces.
     * - content: string or node to go in the element
     * - id: string for ID
     * - element: string for type of element e.g 'div', 'ul', 'li'.
     * - attributions: object (or array of objects) containing props 'attribute' and 'value' (optional).
     */
    constructor({classes = null, content = null, id = null, element = 'div', attributions = null} = {})
    {
        this.element = document.createElement(element);
        if (classes)
        {
            this.addClass(classes);
        }
        if (content)
        {
            this.updateContent(content)
        }
        if (id)
        {
            this.addID(id)
        }
        if (attributions)
        {
            Array.isArray(attributions) ?
                this.addMultipleAttributes(attributions)
                :
                typeof attributions === 'object'
                    ?
                    this.addAttribute(attributions.attribute, attributions.value)
                    :
                    this.throwError('Element constructor: attributions must be an object or an array of objects. {attribute, value}');
        }
        this.element.hidden = false;

        return this;
    }

    checkRequiredParamAndReturnFormatted(param, paramName, methodName, type, props)
    {
        // check the params passed to this method are formatted correctly to do the check
        paramName = paramName ? paramName : '[an undefined parameter]';
        methodName = methodName ? methodName : '[an undefined method]';
        if (typeof paramName !== 'string')
        {
            this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'paramName' argument is not a string`)
        }
        else if (typeof methodName !== 'string')
        {
            this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'methodName' argument is not a string`)
        }

        let listOfTypes;
        listOfTypes = '';
        if (type)
        {
            if (!Array.isArray(type))
            {
                if (typeof type === 'string')
                {
                    type = type.split(' ');
                }
                else
                {
                    this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'type' argument is not an array or a string`)
                }
            }

            const arrayOfValidTypes = ['symbol', 'null', 'object', 'number', 'string', 'boolean', 'array'];
            for (const t of type)
            {
                listOfTypes += (listOfTypes === '') ? t : ` or ${t}`;
                if (arrayOfValidTypes.indexOf(t) === -1)
                {
                    this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: invalid type '${t}' listed in the 'type' argument`)
                }
            }
        }


        if (props)
        {
            if (type.length > 1)
            {
                this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'props' argument passed when the 'type' argument has multiple values (can only check on one type, which is an object)`)
            }
            else if (type[0] !== 'object')
            {
                this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'props' argument passed when the 'type' argument is not an object`)
            }
            else if (!Array.isArray(props))
            {
                if (typeof props === 'string')
                {
                    props = props.split(' ');
                }
                else
                {
                    this.throwError(`CreateElement - checkRequiredParamAndReturnFormatted: 'props' argument is not an array or a string`)
                }
            }
        }


        // check the params
        if (param)
        {
            if (type.indexOf(typeof param) === -1)
            {
                if ((type.indexOf('array') !== -1))
                {
                    if (!Array.isArray(param))
                    {
                        this.throwError(`CreateElement - ${methodName} method: '${paramName}' argument is not a ${listOfTypes}`);
                    }
                }
                else
                {
                    this.throwError(`CreateElement - ${methodName} method: '${paramName}' argument is not a ${listOfTypes}`);
                }
            }
            else if (props)
            {
                for (const p of props)
                {
                    if (!param[p])
                    {
                        this.throwError(`CreateElement - ${methodName} method: '${p}' prop not found in '${paramName}' argument`);
                    }
                }
            }
        }
        else
        {
            this.throwError(`CreateElement - ${methodName} method: '${paramName}' argument not passed`)
        }

        if ((type.indexOf('array') !== -1) && (type.indexOf('string') !== -1))
        {
            if (typeof param === 'string')
            {
                param = param.split(' ');
            }
        }
        else if ((type.indexOf('object') !== -1) && (type.indexOf('string') !== -1))
        {
            if (typeof param === 'string')
            {
                param = document.createTextNode(param)
            }
            else
            {
                param = param.element ? param.element : param;
            }
        }

        return param;
    }

    throwError(message)
    {
        throw new Error(message);
    }

    // ************************************</editor-fold>
    // GENERAL METHODS
    // ************************************<editor-fold>

    /**
     * add and classesToRemove a class simultaneously
     * @param {string / array} classesToAdd  - class(es) to add. REQUIRED.
     * @param {string / array} classesToRemove  - class(es) to remove. REQUIRED.
     */
    addAndRemoveClass(classesToAdd, classesToRemove)
    {
        this.checkRequiredParamAndReturnFormatted(classesToAdd, 'classesToAdd', 'addAndRemoveClass', 'string array');
        this.checkRequiredParamAndReturnFormatted(classesToRemove, 'classesToRemove', 'addAndRemoveClass', 'string array');

        this.addClass(classesToAdd);
        this.removeClass(classesToRemove, false);

        return this;
    }

    /**
     * add attribute to element
     * @param {string} attribute - attribute name. REQUIRED.
     * @param {string} value - attribute value. OPTIONAL.
     */
    addAttribute(attribute, value)
    {
        this.checkRequiredParamAndReturnFormatted(attribute, 'attribute', 'addAttribute', 'string');

        if (attribute)
        {
            if (typeof attribute === 'string')
            {
                (attribute === 'class') ? this.addClass(value) : value ?
                    this.element.setAttribute(attribute, value) : this.element.setAttribute(attribute, '');
            }
            else
            {
                this.throwError('Element.addAttribute: attribute argument must be a single string; use addMultipleAttributes() for multiple attributes')
            }
        }
        else
        {
            this.throwError('Element.addAttribute: no attribute passed');
        }

        return this;
    }

    /**
     * Add class(es) to the element
     * @param {string / array} classes - an array of classes to add to the element
     * - also accepts a single class or a list of classes as a string
     * (each class to be seperated by a space)
     */
    addClass(classes)
    {
        classes = this.checkRequiredParamAndReturnFormatted(classes, 'classes', 'addClass', 'string array');

        for (const c of classes)
        {
            if (c !== '')
            {
                this.element.classList.add(c);
            }
        }

        return this;
    }

    /**
     * add ID to Element
     * @param {string} id - ID to add to element. REQUIRED.
     */
    addID(id)
    {
        this.checkRequiredParamAndReturnFormatted(id, 'id', 'addID', 'string');

        this.element.setAttribute('id', id);

        return this;
    }

    /**
     * add multiple attributes to element
     * @param {array} attributions - array of objects containing attribute and value (optional) props as strings
     */
    addMultipleAttributes(attributions)
    {
        this.checkRequiredParamAndReturnFormatted(attributions, 'attributions', 'addMultipleAttributes', 'object array');

        if (Array.isArray(attributions))
        {
            let i;
            for (i = 0; i < attributions.length; i++)
            {
                this.checkRequiredParamAndReturnFormatted(attributions[i], `attributions[${i}]`, 'addMultipleAttributes', 'object', 'attribute');

                this.addAttribute(attributions[i].attribute, attributions[i].value);
            }
        }
        else
        {
            this.checkRequiredParamAndReturnFormatted(attributions, 'attributions', 'addMultipleAttributes', 'object', 'attribute');

            this.addAttribute(attributions.attribute, attributions.value);
        }

        return this;
    }

    /**
     * add styleS to the element
     * @param {array} arrayOfStyles - array of objects with properties 'property' and 'style'
     */
    addMultipleStyles(arrayOfStyles)
    {
        this.checkRequiredParamAndReturnFormatted(arrayOfStyles, 'arrayOfStyles', 'addMultipleStyles', 'array');

        for (const style of arrayOfStyles)
        {
            this.checkRequiredParamAndReturnFormatted(style, 'style in arrayOfStyles', 'addMultipleStyles', 'object', 'property style');

            this.addStyle(style.property, style.style);
        }

        return this;
    }

    /**
     * add style to the element
     * @param {string} property - style property. REQUIRED.
     * @param {string} value - style value. REQUIRED.
     */
    addStyle(property, value)
    {
        this.checkRequiredParamAndReturnFormatted(property, 'property', 'addStyle', 'string');
        this.checkRequiredParamAndReturnFormatted(value, 'value', 'addStyle', 'string');

        this.element.style[property] = value;

        return this;
    }

    /**
     * animate the element using animate.css classes
     * @param {string} animationEffect - the class of the animationEffect to use. REQUIRED
     * @param {int} speed - speed of the animationEffect. OPTIONAL - defaults to previously set or 1000ms
     * @param {boolean} infinite - repeat the animationEffect infinitely or not. OPTIONAL - defaults to false.
     * @param {boolean} hideElementAfter - hide the element after animation complete. OPTIONAL - defaults to false.
     */
    animateTheElement(animationEffect, speed, infinite, hideElementAfter)
    {
        this.checkRequiredParamAndReturnFormatted(animationEffect, 'animationEffect', 'animateTheElement', 'string');

        if (!this.hasClass('animated'))
        {
            this.addClass('animated')
        }

        if (speed)
        {
            this.element.classList.forEach((className) =>
            {
                if (className.startsWith('speed-'))
                {
                    this.element.classList.remove(className);
                }
            });

            speed = (Math.round(speed / 100) * 100);
            const speedClass = 'speed-' + speed;
            this.addClass(speedClass);
        }

        this.addClass(animationEffect);

        if (infinite)
        {
            this.addClass('infinite');
        }
        else
        {
            this.removeClass('infinite');
            setTimeout(function (element)
            {
                if (hideElementAfter)
                {
                    element.hideTheElement();
                }
                element.removeClass(animationEffect);
            }, speed + 100, this);
        }

        return this;
    }

    /**
     * animate the element in or out of view using animate.css classes
     * @param {string} animationEffect - the class of the animationEffect to use. REQUIRED
     * @param {int} speed - speed of the animationEffect. OPTIONAL - defaults to previously set or 1000ms
     * @param {boolean} hideElementAfter - hide element after animation. OPTIONAL.
     */
    animateTheElementInOrOut(animationEffect, speed, hideElementAfter)
    {
        this.checkRequiredParamAndReturnFormatted(animationEffect, 'animationEffect', ' animateTheElementInOrOut', 'string');

        if (!this.element.hidden || hideElementAfter)
        {
            this.animateTheElement(animationEffect, speed, false, true);
        }
        else
        {
            this.showTheElement(true);
            this.animateTheElement(animationEffect, speed, false, false);
        }

        return this;
    }

    /**
     * append something to the element
     * @param {element / node / string} item - thing to append.
     */
    append(item)
    {
        item = this.checkRequiredParamAndReturnFormatted(item, 'item', 'append', 'object string');

        this.element.appendChild(item);

        return this;
    }

    /**
     * duplicate the element
     * @param {boolean} duplicateChildrenNodes - include children nodes in the duplication
     */
    duplicateTheElement(duplicateChildrenNodes = true)
    {
        const duplicateElement = new Element();
        duplicateElement.element = this.element.cloneNode(duplicateChildrenNodes);
        return duplicateElement;
    }

    /**
     * check if element has class
     * @param {array / string} classes - class(es) to check the element has. REQUIRED.
     * @returns {boolean} - if element has class (true) or not (false)
     */
    hasClass(classes)
    {
        classes = this.checkRequiredParamAndReturnFormatted(classes, 'classes', 'hasClass', 'string array');

        return this.element.classList.contains(classes)
    }

    /**
     * set element style="display: none"
     */
    hideTheElement()
    {
        this.element.style.display = 'none';
        this.element.hidden = true;

        return this;
    }

    /**
     * prepend something to the element
     * @param {element / node / string} item - thing to append
     */
    prepend(item)
    {
        // item = (typeof item !== 'object') ? document.createTextNode(item) : item.element ? item.element : item;
        this.checkRequiredParamAndReturnFormatted(item, 'item', 'append', 'object string');
        this.element.prepend(item);

        return this;
    }

    /**
     * remove all children nodes
     */
    removeAllChildren()
    {
        while (this.element.firstChild)
        {
            this.element.removeChild(this.element.firstChild);
        }
    }

    /**
     * Remove class(es) from the element
     * @param {string / array} classes - an array of classes to remove from the element.  OPTIONAL - will remove all classes if not passed.
     * - also accepts a single class or a list of classes as a string (each class to be separated by a space)
     * @param {boolean} invert - invert the classes, to remove all but those classes.
     */
    removeClass(classes, invert)
    {
        if (!classes || invert)
        {
            this.element.className = '';
        }

        if (classes)
        {
            if (typeof classes === 'string')
            {
                classes = classes.split(' ');
            }

            for (const c of classes)
            {
                if (c !== '')
                {
                    invert ? this.element.classList.add(c) : this.element.classList.remove(c);
                }
            }
        }

        return this;
    }

    /**
     * return the contents of the element
     * @returns {HTMLCollection} - contents of the element
     */
    retrieveContents()
    {
        return this.element.children;
    }

    /**
     * return element as a node (remove from object).
     * @returns {node} - element as a node.
     */
    returnElement()
    {
        return this.element;
    }

    /**
     *  set element style="display: block" (or display type defined in the parameter)
     * @param {boolean} removeStyleAttribute - show the element by removing style attribute all together, rather than changing the display declaration within the style attribute. OPTIONAL.
     * @param {string} display - display declaration (accepts any display css declarations: inline, inline-block etc). OPTIONAL - defaults to block.
     */
    showTheElement(removeStyleAttribute, display = 'block')
    {
        if (removeStyleAttribute)
        {
            this.element.removeAttribute('style');
        }
        else
        {
            this.element.style.display = display;
        }
        this.element.hidden = false;

        return this;
    }

    /**
     * Remove class(es) to the element if they exist, add them if they do not.
     * @param {array / string} classes - an array of classes to add/remove. REQUIRED.
     * - also accepts a single class or a list of classes as a string (each class to be seperated by a space)
     */
    toggleClass(classes)
    {
        classes = this.checkRequiredParamAndReturnFormatted(classes, 'classes', 'toggleClass', 'string array');

        for (const c of classes)
        {
            if (c !== '')
            {
                this.hasClass(c)
                    ?
                    this.element.classList.remove(c)
                    :
                    this.element.classList.add(c);
            }
        }

        return this;
    }

    /**
     * toggle the element in and out of view
     * @param {boolean} removeStyleAttributeOnShow - show the element by removing style attribute all together, rather than changing the display declaration within the style attribute. OPTIONAL.
     * @param {boolean} forceHide - force the element to be hidden. OPTIONAL.
     * @param {boolean} display - display declaration (accepts any display css declarations: inline, inline-block etc) for when shown. OPTIONAL - defaults to block.
     */
    toggleTheElement(removeStyleAttributeOnShow, forceHide, display = 'block')
    {
        if (forceHide)
        {
            this.hideTheElement();
        }
        else
        {
            this.element.hidden ? this.showTheElement(removeStyleAttributeOnShow, display) : this.hideTheElement();
        }

        return this;
    }

    /**
     * remove Element's previous content and then add new content
     * @param {node / string / element} content - content to be added. REQUIRED.
     */
    updateContent(content)
    {
        this.checkRequiredParamAndReturnFormatted(content, 'content', 'updateContent', 'object string');

        this.element.innerHTML = '';
        this.append(content);

        return this;
    }

    /**
     * add new HTML to implement. Useful for templating
     * @param HTML - HTML to update
     */
    updateHTML(HTML)
    {
        this.element.innerHTML = HTML;

        return this;
    }

    /**
     * take the contents of the element and wrap in another element
     * @param {object} wrapperElementOptions - element to wrap contents in:
     * takes all the props the Element constructor takes: {classes, content, id, element, attributions}.
     * OPTIONAL.
     */
    wrapContent(wrapperElementOptions)
    {
        const wrapper = new Element(wrapperElementOptions);
        [...this.returnElement().childNodes].forEach((child) => wrapper.append(child));
        this.append(wrapper.returnElement());

        return this;
    }
}
